import { Checkbox, Form, Input } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React from 'react';
import AutocompleteDropdown from '../../../components/autocomplete-dropdown';
import DrawerButtons from '../../../components/drawer-buttons/index';
import { DropdownOption } from '../../../core/models/DropdownOption';
import { DrawerState } from '../../../core/models/enum';
import {
    confirmDropdownHelper,
    filterDropdownOptions,
} from '../../../helpers/AutocompleteDropdownHelper';
import {
    getCategoriesToDropdownOptions,
    getCompetitionsToDropdownOptions,
} from '../../../helpers/FetchAndTransformHelpers';
import { formItemLayout724 } from '../../../helpers/FormLayoutHelper';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import {
    createOpponentTeam,
    DUMMY_PLAYER_COUNT,
    fillTeamWithDummyPlayers,
    findDefaultCategoryOption,
    getOwnedFederationId,
} from '../../../helpers/StatisticsOpponentHelper';
import { customArrayValidationHelper } from '../../../helpers/ValidationHelper';
import { ApiException } from '../../../utils/api';
import { Props } from './index';

interface State {
    isSaving: boolean;
    categories: DropdownOption[];
    competitions: DropdownOption[];
    selectedCategory?: DropdownOption[];
    selectedCompetition?: DropdownOption[];
    fillWithPlaceholders: boolean;
    progressMessage?: string;
}

class OpponentTeamForm extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            isSaving: false,
            categories: [],
            competitions: [],
            fillWithPlaceholders: true,
        };
    }

    public componentDidMount = (): void => {
        this.getCategories();
        this.getCompetitions();
    };

    private getCategories = async (): Promise<void> => {
        const results = await getCategoriesToDropdownOptions();
        const defaultCategory = findDefaultCategoryOption(results.entities);

        this.setState({
            categories: results.entities,
            selectedCategory: defaultCategory ? [defaultCategory] : undefined,
        });
    };

    private getCompetitions = async (): Promise<void> => {
        const { initialValues } = this.props;
        const results = await getCompetitionsToDropdownOptions(initialValues?.competitionId);

        this.setState({
            competitions: results.entities,
            selectedCompetition: results.selectedEntity,
        });
    };

    private onSubmit = async (values: any): Promise<void> => {
        const { userProfile, handleClose, refreshAfterSave } = this.props;
        const { isSaving, selectedCategory, selectedCompetition, fillWithPlaceholders } =
            this.state;

        // Creating an opponent is a chain of requests, so a second submit would
        // duplicate the whole squad.
        if (isSaving) return;

        if (!selectedCategory || selectedCategory.length === 0) return;

        this.setState({
            isSaving: true,
            progressMessage: 'Creating opponent team...',
        });

        try {
            const team = await createOpponentTeam({
                name: values.name,
                categoryId: parseInt(selectedCategory[0].id, 10),
                federationId: getOwnedFederationId(userProfile),
                competitionId:
                    selectedCompetition && selectedCompetition.length > 0
                        ? parseInt(selectedCompetition[0].id, 10)
                        : undefined,
            });

            if (fillWithPlaceholders) {
                await fillTeamWithDummyPlayers(team.id, (created: number, total: number): void =>
                    this.setState({
                        progressMessage: `Creating placeholder players ${created}/${total}...`,
                    })
                );
            }

            success('Opponent team successfully created.');
            refreshAfterSave();
            // Closing destroys this form, so no state is touched afterwards.
            handleClose(DrawerState.Closed);

            return;
        } catch (error) {
            errorWithUserMessage(error instanceof ApiException ? error.response : error);
        }

        this.setState({
            isSaving: false,
            progressMessage: undefined,
        });
    };

    public render(): React.ReactElement {
        const {
            isSaving,
            categories,
            competitions,
            selectedCategory,
            selectedCompetition,
            fillWithPlaceholders,
            progressMessage,
        } = this.state;
        const { handleClose } = this.props;

        return (
            <Form onFinish={this.onSubmit} {...formItemLayout724}>
                <DrawerButtons
                    isSaving={isSaving}
                    onCancelAction={(): void => handleClose(DrawerState.Closed)}
                />
                <div className="drawer-content">
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[
                            {
                                required: true,
                                message: 'Name field is required!',
                            },
                            { max: 200, message: 'Too long input!' },
                        ]}
                    >
                        <Input placeholder="Opponent team name" />
                    </Form.Item>
                    <Form.Item
                        name="category"
                        label="Category"
                        rules={[
                            {
                                required: true,
                                validator: (_rule: any, value: any, callback: any): void =>
                                    customArrayValidationHelper(
                                        _rule,
                                        value,
                                        callback,
                                        'Category field is required',
                                        selectedCategory
                                    ),
                            },
                        ]}
                    >
                        <AutocompleteDropdown
                            placeholder="Choose category"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(value, categories)
                            }
                            initialValues={selectedCategory}
                            confirmDirty={(options: DropdownOption[]): void =>
                                this.setState({
                                    selectedCategory: confirmDropdownHelper(options),
                                })
                            }
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item name="competition" label="Competition">
                        <AutocompleteDropdown
                            placeholder="Choose competition"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(value, competitions)
                            }
                            initialValues={selectedCompetition}
                            confirmDirty={(options: DropdownOption[]): void =>
                                this.setState({
                                    selectedCompetition: confirmDropdownHelper(options),
                                })
                            }
                            style={{ width: '100%' }}
                            isAllowedToClear
                        />
                    </Form.Item>
                    <Form.Item label="Players">
                        <Checkbox
                            checked={fillWithPlaceholders}
                            onChange={(e: CheckboxChangeEvent): void =>
                                this.setState({ fillWithPlaceholders: e.target.checked })
                            }
                        >
                            {`Fill with ${DUMMY_PLAYER_COUNT} placeholder players`}
                        </Checkbox>
                        <div style={{ color: '#8c8c8c' }}>
                            Placeholders are named Opp 1 to Opp {DUMMY_PLAYER_COUNT}, cap 1 is the
                            goalkeeper. Leave this unchecked to add real players yourself.
                        </div>
                    </Form.Item>
                    {progressMessage && <Form.Item label=" ">{progressMessage}</Form.Item>}
                </div>
            </Form>
        );
    }
}

export default OpponentTeamForm;
